import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useAdminViewContext, ViewMode } from '@/context/AdminViewContext';
import { useIsAdmin } from '@/hooks/use-user-role';

const viewModeLabels: Record<ViewMode, string> = {
  site_admin: 'Site Admin (Full)',
  facility: 'Facility Only',
  collector: 'Collector Only',
  facility_collector: 'Facility + Collector',
};

export const AdminViewSwitcher = () => {
  const { isSiteAdmin } = useIsAdmin();
  const { viewMode, setViewMode, isSimulating } = useAdminViewContext();

  // Only show for actual site admins
  if (!isSiteAdmin) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {isSimulating && (
          <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/30 text-xs">
            Simulating
          </Badge>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Select value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <SelectTrigger className="w-[160px] h-8 text-xs">
                  {isSimulating ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(viewModeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value} className="text-xs">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[250px]">
            <p className="text-xs">
              Test how the UI appears for different user types. 
              This only affects navigation visibility—data access remains unchanged.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};
